import { CalendarIcon, Check, LoaderCircle, TriangleAlert } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import type { DateRange } from 'react-day-picker';

import { Button } from '@/_shared/components/ui/button';
import { Calendar } from '@/_shared/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/_shared/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';
import { cn } from '@/_shared/lib/utils';

import type { DashboardParam } from '../types';

// ─── Date helpers (no date-fns dependency) ───

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function displayDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m) - 1]} ${Number(d)}, ${y}`;
}

function daysAgo(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfMonth(): Date {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function today(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

interface TimeRangePreset {
  label: string;
  from: () => Date;
  to: () => Date;
}

const TIME_PRESETS: TimeRangePreset[] = [
  { label: 'Last 7 days', from: () => daysAgo(7), to: today },
  { label: 'Last 30 days', from: () => daysAgo(30), to: today },
  { label: 'Last 90 days', from: () => daysAgo(90), to: today },
  { label: 'This month', from: startOfMonth, to: today },
];

const DEFAULT_PRESET = TIME_PRESETS[1]!; // Last 30 days

// ─── Props ───

interface Props {
  parameters: DashboardParam[];
  values: Record<string, string | string[]>;
  onChange: (params: Record<string, string | string[]>) => void;
  loadingParamNames?: Set<string>;
}

// ─── Time Range Control ───

function TimeRangeParam({
  param,
  startValue,
  endValue,
  onRangeChange,
}: {
  param: DashboardParam;
  startValue: string;
  endValue: string;
  onRangeChange: (start: string, end: string) => void;
}): React.ReactElement {
  const fromDate = startValue ? new Date(startValue + 'T00:00:00') : DEFAULT_PRESET.from();
  const toDate = endValue ? new Date(endValue + 'T00:00:00') : DEFAULT_PRESET.to();

  const selected: DateRange = { from: fromDate, to: toDate };

  function handleSelect(range: DateRange | undefined): void {
    if (range?.from) {
      onRangeChange(formatDate(range.from), range.to ? formatDate(range.to) : formatDate(range.from));
    }
  }

  function handlePreset(preset: TimeRangePreset): void {
    onRangeChange(formatDate(preset.from()), formatDate(preset.to()));
  }

  const displayFrom = startValue ? displayDate(startValue) : displayDate(formatDate(DEFAULT_PRESET.from()));
  const displayTo = endValue ? displayDate(endValue) : displayDate(formatDate(DEFAULT_PRESET.to()));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'justify-start gap-2 text-left font-normal',
            !startValue && !endValue && 'text-muted-foreground',
          )}
        >
          <CalendarIcon className="h-3.5 w-3.5" />
          <span className="truncate">
            {param.label}: {displayFrom} - {displayTo}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          <div className="space-y-1 border-r border-border p-2">
            {TIME_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs"
                onClick={() => handlePreset(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          <Calendar
            mode="range"
            selected={selected}
            onSelect={handleSelect}
            numberOfMonths={2}
            defaultMonth={fromDate}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Select Control ───

function SelectParam({
  param,
  value,
  onValueChange,
  loading,
}: {
  param: DashboardParam;
  value: string;
  onValueChange: (value: string) => void;
  loading?: boolean;
}): React.ReactElement {
  const options = param.options ?? [];

  if (loading) {
    return (
      <Button variant="outline" size="sm" disabled className="min-w-[8.75rem] gap-2 text-xs">
        <LoaderCircle className="h-3 w-3 animate-spin" />
        {param.label}
      </Button>
    );
  }

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="h-8 w-auto min-w-[8.75rem] text-xs" aria-label={param.label}>
        <SelectValue placeholder={param.label} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ─── Multi-Select Control ───

function MultiSelectParam({
  param,
  selected,
  onSelectedChange,
}: {
  param: DashboardParam;
  selected: string[];
  onSelectedChange: (values: string[]) => void;
}): React.ReactElement {
  const options = (param.options ?? []).filter(
    (opt) => opt.value != null && opt.value !== '' && opt.value !== 'undefined',
  );

  function toggleOption(value: string): void {
    if (selected.includes(value)) {
      onSelectedChange(selected.filter((v) => v !== value));
    } else {
      onSelectedChange([...selected, value]);
    }
  }

  const label =
    selected.length === 0
      ? param.label
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
        : `${param.label} (${selected.length})`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'justify-start gap-1.5 text-left text-xs font-normal',
            options.length === 0 && 'border-amber-400/60 text-amber-700',
          )}
          disabled={options.length === 0}
          title={options.length === 0 ? 'No valid options — check the dashboard spec' : undefined}
        >
          {options.length === 0 && <TriangleAlert className="h-3 w-3" />}
          {options.length === 0 ? `${param.label} — no options` : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="max-h-60 space-y-1 overflow-y-auto">
          {options.map((opt) => {
            const isSelected = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                role="checkbox"
                aria-checked={isSelected}
                className={cn(
                  'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-foreground transition-colors hover:bg-card',
                  isSelected && 'bg-brand/10',
                )}
                onClick={() => toggleOption(opt.value)}
              >
                <div
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-border',
                    isSelected && 'border-brand bg-brand',
                  )}
                >
                  {isSelected && <Check className="text-bg h-3 w-3" />}
                </div>
                <span className="truncate">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Main Component ───

export function DashboardParamBar({ parameters, values, onChange, loadingParamNames }: Props): React.ReactElement {
  // Ref to hold the latest values for stable callbacks
  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  });

  const handleChange = useCallback(
    (key: string, value: string | string[]) => {
      onChange({ ...valuesRef.current, [key]: value });
    },
    [onChange],
  );

  const handleTimeRangeChange = useCallback(
    (name: string, start: string, end: string) => {
      onChange({
        ...valuesRef.current,
        [`${name}_start`]: start,
        [`${name}_end`]: end,
      });
    },
    [onChange],
  );

  if (parameters.length === 0) {
    return <></>;
  }

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border/50 px-6 py-2.5">
      {parameters.map((param) => {
        switch (param.type) {
          case 'time_range': {
            const startKey = `${param.name}_start`;
            const endKey = `${param.name}_end`;
            const startVal = typeof values[startKey] === 'string' ? values[startKey] : '';
            const endVal = typeof values[endKey] === 'string' ? values[endKey] : '';
            return (
              <TimeRangeParam
                key={param.name}
                param={param}
                startValue={startVal}
                endValue={endVal}
                onRangeChange={(s, e) => handleTimeRangeChange(param.name, s, e)}
              />
            );
          }
          case 'select': {
            const raw = values[param.name];
            const val = typeof raw === 'string' ? raw : '';
            return (
              <SelectParam
                key={param.name}
                param={param}
                value={val}
                onValueChange={(v) => handleChange(param.name, v)}
                loading={loadingParamNames?.has(param.name)}
              />
            );
          }
          case 'multi_select': {
            const raw = values[param.name];
            const selected: string[] = Array.isArray(raw) ? raw : [];
            return (
              <MultiSelectParam
                key={param.name}
                param={param}
                selected={selected}
                onSelectedChange={(v) => handleChange(param.name, v)}
              />
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
}
